import { RoundButton } from '@/components/buttons/Buttons';
import RadioButtonInput from '@/components/form-inputs/RadioButtonInput';
import '@/components/program/travel-customization.styles.css';
import { TextBodyExtraExtraSmall } from '@/components/text/Texts';
import { TRAVEL_PROGRAM_FORM } from '@/constants/forms';
import { TRAVEL_LOCATION_CATEGORIES } from '@/constants/travel-program';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import FooterContent from '@/modules/account/components/travel-customization/FooterContent';
import HeaderContent from '@/modules/account/components/travel-customization/HeaderContent';
import { isEmpty } from 'lodash';
import type { DialogProps } from 'primereact/dialog';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import AddLocationModal from './AddLocationModal';
import SecondaryModal from './SecondaryModal';
import { InputController } from '../inputs/InputController';
import DropdownInput from '../inputs/DropdownInput';

interface ModalProps extends DialogProps {
	setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const LocationModal = (props: ModalProps) => {
	const { setVisible, ...rest } = props;
	const [showAddLocationModal, setShowAddLocationModal] =
		useState<boolean>(false);
	const {
		control,
		resetField,
		trigger,
		watch,
		formState: { errors },
		setValue,
	} = useFormContext();
	const locationTypeValue = watch('locationType');
	const locationNameValue = watch('locationName');
	const locationAddressValue = watch('locationAddress');
	const locationRadiusValue = watch('locationRadius');
	const hasLocation =
		!isEmpty(locationNameValue) &&
		!isEmpty(locationAddressValue) &&
		locationRadiusValue > 0;

	const handleOnDelete = () => {
		resetField('locationName');
		resetField('locationAddress');
		resetField('locationRadius');
	};

	const handleOnCancel = useCallback(() => {
		resetField('locationType');
		resetField('locationAllowed');
		resetField('locationName');
		resetField('locationAddress');
		resetField('locationRadius');
		setVisible(false);
	}, [resetField, setVisible]);

	const handleChange = (value: string) => {
		setValue('locationType', value);
	};

	const handleKeyDown = (e: React.KeyboardEvent, value: string) => {
		if (e.key === 'Enter' || e.key === ' ') {
			handleChange(value);
		}
	};

	const handleOnSave = useCallback(() => {
		trigger([
			'locationType',
			'locationAllowed',
			'locationName',
			'locationAddress',
			'locationRadius',
		]).then(() => {
			if (
				(locationTypeValue === 'limited' &&
					!errors.locationAllowed &&
					!errors.locationName &&
					!errors.locationAddress &&
					!errors.locationRadius) ||
				locationTypeValue === 'any'
			) {
				setVisible(false);
			}
		});
	}, [trigger, locationTypeValue, errors, setVisible]);

	const handleOnHide = () => {
		resetField('locationType');
		resetField('locationAllowed');
		resetField('locationName');
		resetField('locationAddress');
		resetField('locationRadius');
		setVisible(false);
	};

	const addLocation = (
		<div className="mt-6">
			{/* TODO not sure about right styles. didnt find this control on figma design */}
			<InputController id="locationAllowed" control={control}>
				<DropdownInput
					options={TRAVEL_LOCATION_CATEGORIES}
					placeholder="Rides allowed on this program"
				/>
			</InputController>
			{/* <DropdownInput
				className="w-full"
				control={control}
				formName={TRAVEL_PROGRAM_FORM}
				id="locationAllowed"
				options={TRAVEL_LOCATION_CATEGORIES}
				placeholder="Rides allowed on this program"
				pt={{
					root: { className: 'tc-dropdown' },
					input: { className: 'tc-dropdown-content' },
					item: { className: 'tc-dropdown-content' },
				}}
			/> */}
			<div className="flex justify-between items-center my-4">
				<h1 className="text-base">Permitted Locations</h1>
				<RoundButton
					className="secondary"
					formName={TRAVEL_PROGRAM_FORM}
					id="add-location"
					label="Add Locations"
					onClick={() => setShowAddLocationModal(true)}
				/>
			</div>
			{!hasLocation ? (
				<div className="bg-secondaryContainer flex justify-center p-7 rounded-md">
					<p className="text-primaryContainer text-xs">
						No location added yet.
					</p>
				</div>
			) : (
				<div className="bg-surfaceContainerLower flex justify-between items-center p-5 rounded-md space-x-4">
					<img
						alt="location"
						src={'/images/svg/Locations-3.svg'}
						width={100}
						height={100}
					/>
					<div className="">
						<h1 className="text-lg mb-3">{locationNameValue}</h1>
						<p className="text-xs text-primary mb-1">
							{locationAddressValue}
						</p>
						<p className="text-xs text-primary">
							{locationRadiusValue}km radius
						</p>
					</div>
					<div className="flex gap-2">
						<RoundButton
							className="secondary"
							icon={'pi pi-pencil'}
							id="location-edit"
							formName={TRAVEL_PROGRAM_FORM}
							onClick={() => setShowAddLocationModal(true)}
						/>
						<RoundButton
							className="secondary"
							icon={'pi pi-trash'}
							id="location-delete"
							formName={TRAVEL_PROGRAM_FORM}
							onClick={handleOnDelete}
						/>
					</div>
				</div>
			)}
			{(errors.locationName ||
				errors.locationAddress ||
				errors.locationRadius) && (
					<TextBodyExtraExtraSmall
						className="block pl-[16px] mt-1 text-error"
						text={VALIDATION_MESSAGES.add_location}
					/>
				)}
		</div>
	);

	return (
		<>
			<SecondaryModal
				{...rest}
				className="w-rem-45"
				draggable={false}
				header={<HeaderContent label="Locations" />}
				footer={
					<FooterContent
						id="locations"
						onClickCancel={handleOnCancel}
						onClickSave={handleOnSave}
					/>
				}
				onHide={handleOnHide}
			>
				<div className="font-poppins">
					<div className="mb-6">
						<p className="text-base text-gray-700 px-2">
							Where should people be able to travel when they ride
							on this travel program?
						</p>
					</div>

					<div>
						<div
							className={
								'flex items-center justify-between py-3 border-b border-slate-300 cursor-pointer'
							}
							onClick={() => handleChange('any')}
							onKeyDown={(e) => handleKeyDown(e, 'any')}
						>
							<RadioButtonInput
								control={control}
								id="locationType"
								formName={TRAVEL_PROGRAM_FORM}
								value={'any'}
							>
								<p className="text-lg text-onSurface">
									Any locations
								</p>
								People can travel from and to anywhere
							</RadioButtonInput>
						</div>
						<div
							className={
								'flex items-center justify-between py-3 border-b border-slate-300 cursor-pointer'
							}
							onClick={() => handleChange('limited')}
							onKeyDown={(e) => handleKeyDown(e, 'limited')}
						>
							<RadioButtonInput
								control={control}
								id="locationType"
								formName={TRAVEL_PROGRAM_FORM}
								value={'limited'}
							>
								<p className="text-lg text-onSurface">
									Limited locations
								</p>
								Set limits on the locations to travel from
							</RadioButtonInput>
						</div>
						{locationTypeValue === 'limited' && addLocation}
					</div>
				</div>
			</SecondaryModal>
			<AddLocationModal
				visible={showAddLocationModal}
				setVisible={setShowAddLocationModal}
				onHide={() => setShowAddLocationModal(false)}
			/>
		</>
	);
};

export default LocationModal;
